import { ContactAddress } from 'src/shared/types/User';

export enum RequestType {
  Add = 'add',
  Edit = 'edit',
  Delete = 'delete',
}

type RecordObject = Record<string, any>;

/**
 * Compares two objects to determine if they have the same values for the specified fields.
 * @param originalObj - The original object to compare.
 * @param newObj - The new object to compare.
 * @param fields - The array of fields to compare.
 * @returns True if the objects have different values for any of the specified fields, false otherwise.
 */
export const AreObjectsFieldsModified = (
  originalObj: RecordObject,
  newObj: RecordObject,
  fields: string[]
): boolean => {
  return fields.some((field) => {
    const original = originalObj[field];
    const current = typeof newObj[field] === 'object' ? newObj[field]?.value : newObj[field];
    return (original ?? '') !== (current ?? '');
  });
};

export const GetBeneficiaryModifiedFields = (
  originalObj: RecordObject,
  newObj: RecordObject,
  fields: string[]
): RecordObject => {
  const modifiedFields: Record<string, { oldValue: unknown; newValue: unknown }> = {};
  fields.forEach((field) => {
    const original = originalObj[field];
    const current = typeof newObj[field] === 'object' ? newObj[field]?.value : newObj[field];

    const normalizedOriginal = original ?? '';
    const normalizedCurrent = current ?? '';

    if (normalizedOriginal !== normalizedCurrent) {
      modifiedFields[field] = normalizedCurrent;
    }
  });

  return modifiedFields;
};

export const CheckIsEmptyAddressItem = (addressItem: ContactAddress) => {
  if (!addressItem) {
    return true;
  }
  const propertiesToSkip = ['addressType', 'stateList', 'countyList', 'countyEnabled', 'datepickerOptions'];
  const isEmpty = Object.keys(addressItem)
    .filter((fieldName) => !propertiesToSkip.includes(fieldName))
    .every((fieldName) => !addressItem[fieldName as keyof ContactAddress]);
  return isEmpty;
};

export const processUpdatedDetails = <
  T extends {
    contactRefId?: number;
    isPrimary?: boolean;
    [key: string]: string | number | boolean | undefined;
  }
>(
  contactList: T[],
  existingList: T[],
  uniqueField: Record<string, number>,
  idField: keyof T,
  modifiedFields: string[]
) => {
  const updatedContacts: {
    [RequestType.Add]: T[];
    [RequestType.Edit]: T[];
    [RequestType.Delete]: number[];
  } = {
    [RequestType.Add]: contactList.filter((item) => !item[idField]).map((item) => ({ ...item, ...uniqueField })),
    [RequestType.Edit]: [],
    [RequestType.Delete]: existingList
      .filter((item) => !contactList.find((newItem) => newItem[idField] === item[idField]))
      .map((item) => item[idField]) as number[],
  };

  const existingPrimary = existingList.find((item) => item.isPrimary);
  const newPrimary = contactList.find((item) => item.isPrimary);
  const hasPrimaryChanged = existingPrimary?.[idField] !== newPrimary?.[idField];

  existingList.forEach((item) => {
    const updatedItem = contactList.find((newItem) => newItem[idField] === item[idField]);
    if (updatedItem) {
      const isPrimary = hasPrimaryChanged && updatedItem[idField] === newPrimary?.[idField];
      if (AreObjectsFieldsModified(item, updatedItem, modifiedFields)) {
        updatedContacts[RequestType.Edit].push({ ...updatedItem });
      } else if (isPrimary) {
        updatedContacts[RequestType.Edit].push({ ...updatedItem, isPrimary });
      }
    }
  });

  return updatedContacts;
};
